import axiosInstance from 'plugins/axios';
import { IBodyResponse } from 'types/type.global';

// ==================== INTERFACES ====================

// Image object
export interface IRewardImage {
    url: string;
}

// Base Reward (raw từ API)
export interface IRewardAttributes {
    id: number;
    name: string;
    image: IRewardImage;
    detail: string;
    created_at: string;
    updated_at: string;
    status: 'active' | 'inactive';
}

// Reward item từ API response
export interface IRewardItem {
    id: string;
    type: string;
    attributes: IRewardAttributes;
}

// Survey Reward Attributes (raw từ API)
export interface ISurveyRewardAttributes {
    id: number;
    quantity: number;
    remaining_quantity: number;
    survey_id: number;
    created_at: string;
    updated_at: string;
    reward: IRewardItem;
}

// Survey Reward Item từ API response
export interface ISurveyRewardItem {
    id: string;
    type: string;
    attributes: ISurveyRewardAttributes;
}

// ==================== PAYLOADS ====================

export interface ICreateRewardPayload {
    name: string;
    image: string;
    detail: string;
}

export interface IUpdateRewardPayload {
    name?: string;
    image?: string;
    detail?: string;
}

export interface ISetupSurveyRewardItem {
    reward_id: number;
    quantity: number;
}

export interface ISetupSurveyRewardsPayload {
    rewards: ISetupSurveyRewardItem[];
}

// ==================== RESPONSES ====================

// Response đã transform
export interface IRewardsResponse {
    rewards: IRewardItem[];
}

// Response đã transform
export interface ISurveyRewardsResponse {
    survey_rewards: ISurveyRewardItem[];
}

/**
 * Lấy danh sách tất cả phần thưởng
 */
export const getRewards = async (): Promise<
    IBodyResponse<IRewardsResponse>
> => {
    const response = await axiosInstance.get('admin/rewards');

    return response.data;
};

// interface danh sách user trúng giải thưởng
export interface IWinningUsersAttributes {
    id: number;
    is_winning: boolean;
    reward: {
        attributes: {
            id: number;
            name: string;
            detail: string;
            created_at: string;
            updated_at: string;
            image: {
                url: string;
            };
        };
    };
    user: {
        id: number;
        name: string;
        email: string;
    };
    status: string;
    updated_at: string;
}

export interface IWinningUsers {
    id: number;
    name: string;
    attributes: IWinningUsersAttributes;
}

export interface IWinningUsersResponse {
    winning_users: IWinningUsers[];
    total_winning: number;
}

/**
 * Tạo phần thưởng mới
 */
export const createReward = async (
    payload: ICreateRewardPayload | FormData
): Promise<IBodyResponse<IRewardItem>> => {
    const response = await axiosInstance.post('admin/rewards', payload);
    return response.data;
};

/**
 * Cập nhật phần thưởng
 */
export const updateReward = async (
    rewardId: number,
    payload: IUpdateRewardPayload | FormData
): Promise<IBodyResponse<IRewardItem>> => {
    const response = await axiosInstance.put(
        `admin/rewards/${rewardId}`,
        payload
    );
    return response.data;
};

// 2. Quản lý Survey Rewards

/**
 * Lấy danh sách phần thưởng của survey
 */
export const getSurveyRewards = async (
    surveyId: number
): Promise<IBodyResponse<ISurveyRewardsResponse>> => {
    const response = await axiosInstance.get(
        `admin/surveys/${surveyId}/survey_rewards`
    );

    return response.data;
};

/**
 * Setup phần thưởng cho survey
 */
export const setupSurveyRewards = async (
    surveyId: number,
    payload: ISetupSurveyRewardsPayload
): Promise<IBodyResponse<ISurveyRewardsResponse>> => {
    const response = await axiosInstance.post(
        `admin/surveys/${surveyId}/survey_rewards/setup`,
        payload
    );

    return response.data;
};

// 3. Quản lý Reward Draws (Lịch sử quay thưởng)

/**
 * Lấy danh sách quay thưởng của survey
 */
// export const getRewardDraws = async (
//     surveyId: number
// ): Promise<IBodyResponse<IRewardDrawsResponse>> => {
//     const response = await axiosInstance.get(
//         `admin/surveys/${surveyId}/reward_draws`
//     );
//     return response.data;
// };

/**
 * Lấy danh sách user trúng giải thưởng
 */
export const getWinningUsers = async (
    surveyId: number
): Promise<IBodyResponse<IWinningUsersResponse>> => {
    const response = await axiosInstance.get(
        `admin/surveys/${surveyId}/reward_draws/winning_users`
    );
    return response.data;
};

// // ==================== USER APIs (v1) ====================

/**
 * Lấy danh sách phần thưởng của survey (User)
 */
export interface IPreviewSurveyReward {
    id: number;
    type: string;
    attributes: {
        created_at: string;
        detail: string;
        id: string;
        image: string;
        name: string;
        quantity: number;
        status: string;
        survey_reward_id: number;
        total_quantity: number;
        updated_at: string;
    };
}
export interface IPreviewSurveyRewardsResponse {
    rewards: IPreviewSurveyReward[];
}
export const getUserSurveyRewards = async (
    token: string | null,
    surveyId: number | string
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<IBodyResponse<IPreviewSurveyRewardsResponse>> => {
    const url = token
        ? `preview_survey/${surveyId}/rewards?token=${token}`
        : `preview_survey/${surveyId}/rewards`;
    const response = await axiosInstance.get(url);
    return response.data;
};

/**
 * Quay thưởng
 */
export interface IUserDrawRewardResponse {
    is_winning: boolean;
    reward_draw: {
        attributes: {
            id: number;
            status: string;
            created_at: string;
            updated_at: string;
            reward: {
                id: number;
                type: string;
                attributes: {
                    id: number;
                    name: string;
                    detail: string;
                    status: string;
                    created_at: string;
                    updated_at: string;
                    image: string;
                };
            };
            survey_id: number;
            reward_id: number;
            is_winning: boolean;
        };
    };
}
export const drawReward = async (
    token: string | null,
    surveyId: number | string
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<IBodyResponse<IUserDrawRewardResponse>> => {
    const url = token
        ? `preview_survey/${surveyId}/rewards/draw?token=${token}`
        : `preview_survey/${surveyId}/rewards/draw`;
    const response = await axiosInstance.post(url);
    return response.data;
};
